import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import Reveal from "@/components/Reveal";

const CATEGORIES = ["All", "Smartphones", "Audio", "Laptops", "Wearables", "Accessories"];
const MAX_PRICE = 3500;

export default function Shop() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState(params.get("category") || "All");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [maxPrice, setMaxPrice] = useState(MAX_PRICE);
  const [inStock, setInStock] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    const cat = params.get("category");
    if (cat) setCategory(cat);
  }, [params]);

  const filtered = useMemo(() => {
    if (!products) return [];
    let list = products.filter(
      (p) =>
        (category === "All" || p.category === category) &&
        p.price <= maxPrice &&
        (!inStock || p.stock > 0) &&
        (!search.trim() ||
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.tagline.toLowerCase().includes(search.toLowerCase()))
    );
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price_desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "rating") list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, category, search, sort, maxPrice, inStock]);

  const reset = () => {
    setCategory("All");
    setSearch("");
    setMaxPrice(MAX_PRICE);
    setInStock(false);
    setSort("featured");
  };

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 lg:px-10" data-testid="shop-page">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-voltaic-cyan">Catalogue // Drop 07</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
          The <span className="text-outline">vault</span>
        </h1>
      </Reveal>

      {/* Controls */}
      <Reveal delay={0.1}>
        <div className="mt-10 flex flex-col gap-4 rounded-2xl border border-voltaic-line bg-voltaic-surface/60 p-5 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-voltaic-muted" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search devices..."
              className="field !pl-11"
            />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-muted">Max ${maxPrice}</span>
              <input
                data-testid="price-range-slider"
                type="range"
                min={79}
                max={MAX_PRICE}
                step="10"
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-36"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-muted">
              <input
                data-testid="in-stock-toggle"
                type="checkbox"
                checked={inStock}
                onChange={(e) => setInStock(e.target.checked)}
                className="h-4 w-4 accent-[#00F0FF]"
              />
              In stock
            </label>
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="field !w-auto !py-2.5 font-mono text-[11px] uppercase tracking-[0.15em]">
              <option value="featured">Featured</option>
              <option value="price_asc">Price: low to high</option>
              <option value="price_desc">Price: high to low</option>
              <option value="rating">Top rated</option>
            </select>
          </div>
        </div>
      </Reveal>

      {/* Category chips */}
      <div className="mt-6 flex flex-wrap gap-2.5" data-testid="category-chips">
        {CATEGORIES.map((c) => {
          const count = (products || []).filter((p) => c === "All" || p.category === c).length;
          return (
            <button
              key={c}
              data-testid={`category-chip-${c.toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-all duration-200 ${
                category === c
                  ? "border-voltaic-cyan bg-voltaic-cyan/10 text-voltaic-cyan"
                  : "border-voltaic-line text-voltaic-muted hover:border-voltaic-linebright hover:text-voltaic-ink"
              }`}
            >
              {c} <span className="opacity-60">({count})</span>
            </button>
          );
        })}
      </div>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-voltaic-muted" data-testid="results-count">
        {filtered.length} device{filtered.length === 1 ? "" : "s"} found
      </p>

      {/* Grid */}
      {products === null ? (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl border border-voltaic-line bg-voltaic-surface" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-4 rounded-2xl border border-dashed border-voltaic-line py-20 text-center" data-testid="shop-empty-state">
          <p className="font-display text-xl font-bold">No devices match that signal.</p>
          <p className="text-sm text-voltaic-muted">Loosen the filters and try again.</p>
          <button data-testid="shop-reset-button" onClick={reset} className="mt-2 rounded-full bg-voltaic-cyan px-6 py-2.5 text-sm font-semibold text-voltaic-bg">
            Reset filters
          </button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {filtered.map((p, i) => (
            <ProductCard key={p.id} product={p} onSelect={setSelected} index={i} />
          ))}
        </div>
      )}

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </main>
  );
}
